import React from 'react'
import { interceptExternalLinkClick } from '../lib/nativeShell'
import { AnimatePresence, motion } from 'motion/react'
import { Checkin, Feature, ManualOverrideType } from '../api/client'
import { LocationDetailValue } from './checkin/LocationDetailModal'
import FeatureLockBadge from './FeatureLockBadge'
import { checkinFeatureOwner } from '../utils/checkinFeatures'
import { googleMapsUrl } from '../utils/googleMaps'

interface LocationChipsProps {
  checkin: Checkin
  collectionItems: Record<string, any>
  /** Page-level feature list. Used to resolve the user-global Locations
   *  feature_id without depending on the per-checkin `feature_details`,
   *  which is reconstructed at fetchData time and goes stale when a parse
   *  attaches the first-ever Locations row to a previously-loaded checkin.
   *  See PersonChips for the same fix. */
  features?: Feature[]
  /** Stable row key of the chip whose pin is currently selected on the
   *  sibling map. The matching chip renders with an amber accent so the
   *  user can tell which place a tapped pin represents without opening the
   *  modal. */
  highlightedKey?: string | null
  /** Chip click → emit the row's `LocationDetailValue` so the parent can
   *  open `LocationDetailModal` itself. The modal lives one level up
   *  (`CheckinItem`) because the expanded-map flow also needs to open it,
   *  and we only want one modal instance regardless of source. */
  onChipClick?: (value: LocationDetailValue) => void
}

export interface RenderedLocation {
  key: string
  label: string
  /** Tap target for the chip. Place rows always get a detailValue (the modal
   *  hides its map block when coords are missing — common right after a
   *  parser CREATE_NEW before the Google resolver has filled coords in).
   *  Coords-only rows pass through bare lat/lng. `null` only on rows we
   *  can't resolve at all (kept for the outbound fallback below). */
  detailValue: LocationDetailValue | null
  externalHref: string
}

const LOCATIONS_FEATURE_TITLE_INTERNAL = 'Locations'

// Cap chip labels so `whitespace-nowrap` doesn't let a long place name overflow.
const MAX_LABEL_LEN = 30
const truncateLabel = (s: string): string =>
  s.length > MAX_LABEL_LEN ? `${s.slice(0, MAX_LABEL_LEN - 1)}…` : s

export const findLocationsFeatureId = (
  checkin: Checkin,
  features?: Feature[],
): number | null => {
  const fromGlobal = (features || []).find(
    (f) => !f.project_id && f.title === LOCATIONS_FEATURE_TITLE_INTERNAL,
  )
  if (fromGlobal) return fromGlobal.id
  const fromCheckin = (checkin.feature_details || []).find(
    (f) => !f.project_id && f.title === LOCATIONS_FEATURE_TITLE_INTERNAL,
  )
  return fromCheckin ? fromCheckin.id : null
}

/**
 * Returns true when the checkin has at least one location worth rendering —
 * i.e. an active row of the user-global Locations feature with either a
 * `reference_value` (private Place) or a `(lat, lng)` pair. Used by parents
 * to decide whether to render any wrapper at all.
 */
export function hasLocationChips(checkin: Checkin, features?: Feature[]): boolean {
  const locationsFeatureId = findLocationsFeatureId(checkin, features)
  if (locationsFeatureId == null) return false
  return checkin.checkin_features.some(
    (cf) =>
      cf.feature_id === locationsFeatureId &&
      cf.manual_override !== ManualOverrideType.DELETE &&
      (cf.reference_value != null ||
        (cf.latitude != null && cf.longitude != null))
  )
}

/**
 * Walks the checkin's active Locations rows and produces one
 * `RenderedLocation` per resolvable row — Place refs (with name) and
 * coords-only rows. Used by the chip renderer here AND by the expanded-map
 * modal so both surfaces agree on what's a clickable location and what its
 * `LocationDetailValue` looks like.
 *
 * `features` is optional: when supplied, the page-level features list is
 * preferred over `checkin.feature_details` to dodge the post-parse staleness
 * window where feature_details lags behind checkin_features.
 */
export function extractCheckinLocationDetails(
  checkin: Checkin,
  collectionItems: Record<string, any>,
  features?: Feature[],
): RenderedLocation[] {
  const locationsFeatureId = findLocationsFeatureId(checkin, features)
  if (locationsFeatureId == null) return []
  const activeRows = checkin.checkin_features.filter(
    (cf) =>
      cf.feature_id === locationsFeatureId &&
      cf.manual_override !== ManualOverrideType.DELETE
  )
  const rendered: RenderedLocation[] = []
  for (const row of activeRows) {
    if (row.reference_value != null) {
      const item = collectionItems[String(row.reference_value)]
      const name = item?.data?.name
      if (!name) continue
      const lat = item?.data?.lat
      const lng = item?.data?.lng
      const googlePlaceId = item?.data?.google_place_id
      const address = item?.data?.address
      rendered.push({
        key: `ref-${row.reference_value}`,
        label: name,
        detailValue: {
          collectionItemId: row.reference_value,
          lat: lat ?? undefined,
          lng: lng ?? undefined,
          name,
          address,
          googlePlaceId,
        },
        externalHref: googleMapsUrl({ googlePlaceId, lat, lng, name }),
      })
    } else if (row.latitude != null && row.longitude != null) {
      rendered.push({
        key: `coords-${row.latitude}-${row.longitude}`,
        label: `${row.latitude.toFixed(2)}°, ${row.longitude.toFixed(2)}°`,
        detailValue: { lat: row.latitude, lng: row.longitude },
        externalHref: googleMapsUrl({ lat: row.latitude, lng: row.longitude }),
      })
    }
  }
  return rendered
}

/**
 * Read-only display of locations attached to a checkin. Tapping a chip
 * emits its `LocationDetailValue` to the parent via `onChipClick`, which
 * is responsible for actually rendering `LocationDetailModal`.
 *
 * Source: the user-global Locations feature (`project_id IS NULL`,
 * title='Locations'). Resolution prefers the page-level `features` list
 * and falls back to the checkin's own `feature_details`.
 */
const LocationChips: React.FC<LocationChipsProps> = ({ checkin, collectionItems, features, highlightedKey, onChipClick }) => {
  const rendered = extractCheckinLocationDetails(checkin, collectionItems, features)
  if (rendered.length === 0) return null

  // Once, beside the strip — the lock belongs to the Locations feature on this
  // checkin, not to any one place. See `docs/list_tagging.md`.
  const locationsFeatureId = findLocationsFeatureId(checkin, features)
  const owner =
    locationsFeatureId != null ? checkinFeatureOwner(checkin, locationsFeatureId) : 'parser'

  const chipMotion = {
    style: { transformOrigin: 'left center' },
    initial: { opacity: 0, scale: 0.6 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.6 },
    transition: { type: 'spring' as const, stiffness: 500, damping: 30 },
    whileHover: { y: -1 },
    whileTap: { scale: 0.95 },
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <AnimatePresence initial={true}>
        {rendered.map((loc) => {
          const isHighlighted = highlightedKey === loc.key
          const className = isHighlighted
            ? 'inline-flex items-center rounded-full text-xs font-medium bg-amber-100 text-amber-900 ring-2 ring-amber-400 px-3 py-1 transition-colors cursor-pointer whitespace-nowrap'
            : 'inline-flex items-center rounded-full text-xs font-medium bg-red-200 text-red-900 hover:bg-red-300 px-3 py-1 transition-colors cursor-pointer whitespace-nowrap'
          if (loc.detailValue) {
            const value = loc.detailValue
            return (
              <motion.button
                key={loc.key}
                type="button"
                {...chipMotion}
                onClick={() => onChipClick?.(value)}
                className={className}
                title={`View ${loc.label} on a map`}
              >
                <span className="mr-1">📍</span>
                {truncateLabel(loc.label)}
              </motion.button>
            )
          }
          return (
            <motion.a
              key={loc.key}
              {...chipMotion}
              href={loc.externalHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={interceptExternalLinkClick}
              className={className}
              title={`Open ${loc.label} on Google Maps`}
            >
              <span className="mr-1">📍</span>
              {truncateLabel(loc.label)}
            </motion.a>
          )
        })}
      </AnimatePresence>
      {owner !== 'parser' && (
        <FeatureLockBadge
          className="ml-0"
          owner={owner}
          title="Manually set — a re-parse leaves these places alone"
        />
      )}
    </div>
  )
}

export default LocationChips
